import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import * as moment from 'moment';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { Subject } from 'rxjs';
import { AlertService } from 'src/app/_services/alert.service';
import { CalendarService } from 'src/app/_services/calendar.service';
import { RequestService } from 'src/app/_services/request.service';
import { BaseComponent } from '../../../base/base.component';

@Component({
  selector: 'app-RequestPermission',
  templateUrl: './RequestPermission.component.html',
  styleUrls: [
    './RequestPermission.component.css',
    '../../review-modal/review-modal.component.css'
  ]
})
export class RequestPermissionComponent
  extends BaseComponent
  implements OnInit
{
  private readonly alertService: AlertService = inject(AlertService);
  private readonly calendarService: CalendarService = inject(CalendarService);
  public rs: RequestService = inject(RequestService);
  @Output() saved = new EventEmitter<{ success: boolean; date: Date }>();
  // input arrivato dal componente Calendar
  readonly modalData: any = inject(NZ_MODAL_DATA);

  request?: any;
  user: any;
  date: string;
  edit: boolean;
  userEventDates: any[] = [];
  canCreatePastRequest = false;
  private readonly partTimeNonWorkingDateCache = new Map<string, boolean>();
  private readonly loadedPartTimeWeeks = new Set<string>();
  private readonly loadingPartTimeWeeks = new Set<string>();

  MIN_DURATION = 30;
  readonly requestsPerStep = 2;
  defaultTime = new Date(new Date().setHours(9, 0, 0, 0)); // 09:00 senza minuti casuali
  requestForm: FormGroup;
  timePickerOpen: boolean = false;
  currentStep = 0;
  private readonly destroy$: Subject<void> = new Subject<void>();
  public formBuilder: FormBuilder = inject(FormBuilder);

  constructor(private readonly modalRef: NzModalRef) {
    super();
  }

  ngOnInit() {
    this.request = this.modalData.request;
    this.user = this.modalData.user;
    this.date = this.modalData.date;
    this.edit = this.modalData.edit;
    this.userEventDates = this.modalData.userEventDates ?? [];
    this.canCreatePastRequest = !!this.modalData.canCreatePastRequest;

    this.seedPartTimeWeekFromCalendarPayload();
    this._initForm();
    this.addRequest();
    if (this.request && this.edit) {
      this.patchForm(this.request);
    }

    const firstControl = (this.requestForm.get('dataArray') as FormArray).at(0);
    this.loadUserEventDates(firstControl?.get('date')?.value ?? null);
  }

  OnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Inizializzo il form
   */
  _initForm() {
    this.requestForm = this.formBuilder.group({
      dataArray: this.formBuilder.array([], this.duplicateTimeValidator()),
      event_type_id: 8
    });
  }

  /**
   * Ottieni i controlli del formArray: dataArray nel modulo di richiesta permesso
   */
  get newRequestControls() {
    return (this.requestForm.get('dataArray') as FormArray).controls;
  }

  get totalSteps(): number {
    const steps = Math.ceil(
      this.newRequestControls.length / this.requestsPerStep
    );
    return Math.max(steps, 1);
  }

  // Usa gli eventi già caricati dal BE e tiene solo quelli del giorno selezionato nella riga corrente.
  getExistingEventsForRequest(index: number): any[] {
    const control = (this.requestForm.get('dataArray') as FormArray)?.at(index);
    const selectedDate = control?.get('date')?.value;
    if (!selectedDate) {
      return [];
    }
    const formattedDate = moment(selectedDate).format('YYYY-MM-DD');
    return this.userEventDates.filter(
      (event: any) => event.date === formattedDate
    );
  }

  // una richiesta di permesso non può proseguire se nello stesso giorno esistono già ferie.
  hasBlockingConflict(index: number): boolean {
    return (
      !this.edit &&
      this.getExistingEventsForRequest(index).some(
        (event: any) => event.eventType === 'Ferie'
      )
    );
  }

  // Serve a disabilitare il bottone Invia se confiltto esiste
  get hasBlockingConflicts(): boolean {
    return this.newRequestControls.some((_, index) =>
      this.hasBlockingConflict(index)
    );
  }

  // Se c'è conflitto mostra il messaggio bloccante; altrimenti mostra il riepilogo degli eventi già presenti.
  getExistingEventsAlertText(index: number): string {
    if (this.hasBlockingConflict(index)) {
      return 'Hai già ferie in questo periodo. Eliminale per procedere o modifca le date della richiesta.';
    }
    const formatted = this.getExistingEventsForRequest(index).map(event =>
      this.formatExistingEvent(event)
    );
    return [...new Set(formatted)].join(' • ');
  }

  // Uniforma il testo mostrato nell'avviso per eventi puntuali o con fascia oraria.
  formatExistingEvent(event: any): string {
    if (event?.startTime && event?.endTime) {
      const start = event.startTime.slice(0, 5);
      const end = event.endTime.slice(0, 5);
      return start === end
        ? `${event.eventType} ${start}`
        : `${event.eventType} ${start} - ${end}`;
    }
    return event?.eventType ?? '';
  }

  // Richiama il BE quando cambia la data e aggiorna modale
  private loadUserEventDates(date: Date | null): void {
    if (!date || !this.user?.id) {
      return;
    }

    this.calendarService
      .getUserEventDates(moment(date).format('YYYY-MM-DD'), this.user.id)
      .subscribe({
        next: (res: any) => {
          this.userEventDates = res?.events ?? [];
        }
      });
  }

  get visibleRequests(): Array<{ control: AbstractControl; index: number }> {
    const start = this.currentStep * this.requestsPerStep;
    return this.newRequestControls
      .slice(start, start + this.requestsPerStep)
      .map((control, offset) => ({ control, index: start + offset }));
  }

  get stepDots(): number[] {
    return Array.from({ length: this.totalSteps }, (_, i) => i);
  }

  trackByVisibleRequest(
    _: number,
    item: { control: AbstractControl; index: number }
  ): AbstractControl {
    return item.control;
  }

  trackByNumber(_: number, value: number): number {
    return value;
  }

  /**
   * Override locale della validazione date per questo componente:
   * permette il giorno corrente e blocca solo date passate, weekend e festività.
   * @param current data valutata dal date picker
   * @returns true se la data deve essere disabilitata
   */
  override disablePastAndWeekendDates = (current: Date): boolean => {
    if (this.isPartTimeNonWorkingDate(current)) {
      return true;
    }

    const today = moment();
    const date = moment(current);

    const isPast = date.isBefore(today, 'day');

    return (
      (!this.canCreatePastRequest && isPast) ||
      this.utilityService.disabledDate(current)
    );
  };

  /**
   * Inizializza la cache FE con la settimana già presente nel payload
   * calendario, compresi eventuali override pending mostrati in griglia.
   */
  private seedPartTimeWeekFromCalendarPayload(): void {
    const calendarDays = this.modalData?.user?.days ?? [];

    calendarDays.forEach((day: any) => {
      if (!day?.date || !day?.part_time_schedule?.is_part_time) {
        return;
      }

      this.partTimeNonWorkingDateCache.set(
        day.date,
        !day.part_time_schedule.is_working_day
      );
      this.loadedPartTimeWeeks.add(
        moment(day.date).startOf('isoWeek').format('YYYY-MM-DD')
      );
    });
  }

  /**
   * Recupera al bisogno la settimana effettiva part-time dell'utente quando
   * il datepicker si sposta fuori dalla settimana già caricata a calendario.
   */
  private ensurePartTimeWeekLoaded(date: Date | null): void {
    if (!date || !this.user?.id) {
      return;
    }

    const weekStart = moment(date).startOf('isoWeek').format('YYYY-MM-DD');

    if (
      this.loadedPartTimeWeeks.has(weekStart) ||
      this.loadingPartTimeWeeks.has(weekStart)
    ) {
      return;
    }

    this.loadingPartTimeWeeks.add(weekStart);

    this.rs.getPartTimeWeek(this.user.id, weekStart).subscribe({
      next: (response: any) => {
        const result = response?.result;
        const days = result?.days ?? [];
        const isPartTime = !!result?.contract?.is_part_time;

        if (isPartTime) {
          days.forEach((day: any) => {
            const dateKey = moment(weekStart)
              .add((day.weekday ?? 1) - 1, 'days')
              .format('YYYY-MM-DD');

            this.partTimeNonWorkingDateCache.set(dateKey, !day.is_working_day);
          });
        }

        this.loadedPartTimeWeeks.add(weekStart);
        this.loadingPartTimeWeeks.delete(weekStart);
      },
      error: () => {
        this.loadingPartTimeWeeks.delete(weekStart);
      }
    });
  }

  /**
   * Indica se il giorno selezionato è non lavorativo nella distribuzione
   * part-time effettiva dell'utente.
   */
  private isPartTimeNonWorkingDate(date: Date | null): boolean {
    if (!date) {
      return false;
    }

    const dateKey = moment(date).format('YYYY-MM-DD');
    this.ensurePartTimeWeekLoaded(date);

    return this.partTimeNonWorkingDateCache.get(dateKey) === true;
  }

  /**
   * Aggiunge una nuova richiesta di permesso nel formRequest
   */
  addRequest(): void {
    const dataArray = this.requestForm.get('dataArray') as FormArray;
    // passo la data che arriva dal componente padre
    const newDate = this.date
      ? new Date(this.date.replace(/\//g, '-'))
      : new Date();

    let defaultStartTime: Date | null = null;
    let defaultEndTime: Date | null = null;
    let dateValue: Date | null = newDate;

    if (dataArray.length >= 1) {
      // Se è la seconda riga, svuoto il campo date
      dateValue = null;
    }

    if (dataArray.length > 0) {
      const sameDayRequests = dataArray.controls.filter(ctrl => {
        const ctrlDate = ctrl.get('date')?.value;
        return ctrlDate?.toDateString() === newDate.toDateString();
      });

      if (sameDayRequests.length === 0) {
        const latestEndTime = sameDayRequests
          .map(ctrl => ctrl.get('endTime')?.value)
          .filter(t => !!t)
          .sort((a: Date, b: Date) => a.getTime() - b.getTime())
          .pop();

        if (latestEndTime) {
          defaultStartTime = new Date(latestEndTime);
          defaultStartTime.setMinutes(defaultStartTime.getMinutes() + 15);
          defaultEndTime = new Date(defaultStartTime);
          defaultEndTime.setMinutes(defaultEndTime.getMinutes() + 30);
        }
      }
    }

    const group = this.formBuilder.group(
      {
        date: [dateValue, Validators.required],
        startTime: [defaultStartTime, Validators.required],
        endTime: [defaultEndTime, Validators.required],
        notes: [null, Validators.required]
      },
      { validators: this.minimumDurationValidator(30) }
    );

    if (dataArray.length === 0) {
      group.patchValue({ startTime: null, endTime: null });
    }

    dataArray.push(group);
    this.currentStep = this.totalSteps - 1;
  }

  /**
   * Patch del form in caso di modifica di un permesso
   * @param data dati della richiesta di permesso da modificare
   */
  patchForm(data: any): void {
    // prima resetto l'array
    const arrayControl = this.requestForm.get('dataArray') as FormArray;
    arrayControl.clear();

    // aggiungo un nuovo gruppo per la richiesta
    arrayControl.push(
      this.formBuilder.group(
        {
          date: [new Date(data.start_date), Validators.required],
          startTime: [
            new Date(data.start_date + 'T' + data.start_time),
            Validators.required
          ],
          endTime: [
            new Date(data.start_date + 'T' + data.end_time),
            Validators.required
          ],
          notes: [data.note ?? null, Validators.required]
        },
        { validators: this.minimumDurationValidator(30) }
      )
    );

    // patcho le altre proprietà
    this.requestForm.patchValue({
      event_type_id: 8 // fisso come nel form
    });
  }

  /**
   *
   * @param minutes passo i minuti e ne calcosa la differenza tra startTime e endTime
   * @returns
   */
  minimumDurationValidator(minutes: number): ValidatorFn {
    return (group: AbstractControl): { [key: string]: any } | null => {
      const start = group.get('startTime')?.value;
      const end = group.get('endTime')?.value;

      if (
        !start ||
        !end ||
        !(start instanceof Date) ||
        !(end instanceof Date)
      ) {
        return null; // Skip if incomplete
      }

      const diff = (end.getTime() - start.getTime()) / (1000 * 60); // minutes
      return diff < minutes ? { minDuration: true } : null;
    };
  }

  /**
   *
   * @param index passo l'indice della row dove sto settando l'orario di inizio
   * @returns disabilita le ore già passate
   */
  getDefaultStartTime(index: number): Date {
    const requestGroup = (this.requestForm.get('dataArray') as FormArray).at(
      index
    );
    const selectedDate: Date = requestGroup.get('date')?.value;

    if (!selectedDate) return new Date(new Date().setHours(9, 0, 0, 0)); // fallback

    const today = new Date();
    const isToday = selectedDate.toDateString() === today.toDateString();

    if (isToday) {
      const now = new Date();
      const rounded = new Date(now);
      const minutes = now.getMinutes();
      const roundedMinutes = Math.ceil(minutes / 15) * 15;

      // Se supera 60, porta all’ora successiva
      if (roundedMinutes === 60) {
        rounded.setHours(rounded.getHours() + 1);
        rounded.setMinutes(0);
      } else {
        rounded.setMinutes(roundedMinutes);
      }

      rounded.setSeconds(0);
      rounded.setMilliseconds(0);

      return rounded;
    }

    // Se non è oggi, default alle 09:00
    return new Date(selectedDate.setHours(9, 0, 0, 0));
  }

  /**
   *
   * @returns controlla se le ore selezionate siano diverse nel caso la richiesta sia fatta per lo stesso giorno
   */
  duplicateTimeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control instanceof FormArray)) return null;

      const seen = new Map<string, number[]>();
      const validRows: Array<{
        index: number;
        date: Date;
        start: Date;
        end: Date;
      }> = [];

      control.controls.forEach((group, index) => {
        const date = group.get('date')?.value;
        const start = group.get('startTime')?.value;
        const end = group.get('endTime')?.value;

        if (!(date && start && end)) return;

        validRows.push({ index, date: new Date(date), start, end });

        const key = `${new Date(date).toDateString()}_${start.getHours()}:${start.getMinutes()}_${end.getHours()}:${end.getMinutes()}`;

        if (!seen.has(key)) {
          seen.set(key, [index]);
        } else {
          seen.get(key)!.push(index);
        }
      });

      // Rimuove tutti gli errori duplicati precedenti
      control.controls.forEach(group => {
        if (group.errors?.['duplicate'] || group.errors?.['overlap']) {
          const newErrors = { ...group.errors };
          delete newErrors['duplicate'];
          delete newErrors['overlap'];
          if (Object.keys(newErrors).length === 0) {
            group.setErrors(null);
          } else {
            group.setErrors(newErrors);
          }
        }
      });

      // Applica errore solo alle righe duplicate (non la prima)
      seen.forEach(indexes => {
        if (indexes.length > 1) {
          indexes.slice(1).forEach(i => {
            const group = control.at(i);
            group.setErrors({ ...group.errors, duplicate: true });
          });
        }
      });

      // Sovrapposizione parziale/totale nello stesso giorno
      for (let i = 0; i < validRows.length; i++) {
        for (let j = i + 1; j < validRows.length; j++) {
          const first = validRows[i];
          const second = validRows[j];

          if (first.date.toDateString() !== second.date.toDateString())
            continue;

          const overlap =
            first.start.getTime() < second.end.getTime() &&
            second.start.getTime() < first.end.getTime();
          const exactSameInterval =
            first.start.getTime() === second.start.getTime() &&
            first.end.getTime() === second.end.getTime();

          if (overlap && !exactSameInterval) {
            control.at(first.index).setErrors({
              ...control.at(first.index).errors,
              overlap: true
            });
            control.at(second.index).setErrors({
              ...control.at(second.index).errors,
              overlap: true
            });
          }
        }
      }

      return null;
    };
  }

  /**
   * Restituisce le fasce orarie già occupate da altre richieste
   * aventi la stessa data della riga corrente.
   * @param index indice della richiesta corrente
   * @returns intervalli occupati espressi in minuti da mezzanotte
   */
  private getSameDayTimeRanges(
    index: number
  ): Array<{ startMinutes: number; endMinutes: number }> {
    const current = this.dataArray.at(index);
    const selectedDate = current?.get('date')?.value as Date;

    if (!(selectedDate instanceof Date)) return [];

    // intervalli già presenti nel form per lo stesso giorno
    const formRanges = this.dataArray.controls
      .map((ctrl, i) => ({ ctrl, i }))
      .filter(({ i, ctrl }) => {
        if (i === index) return false;
        const date = ctrl.get('date')?.value;
        const start = ctrl.get('startTime')?.value;
        const end = ctrl.get('endTime')?.value;

        return (
          date instanceof Date &&
          start instanceof Date &&
          end instanceof Date &&
          date.toDateString() === selectedDate.toDateString()
        );
      })
      .map(({ ctrl }) => ({
        startMinutes: this.toMinutes(ctrl.get('startTime')?.value),
        endMinutes: this.toMinutes(ctrl.get('endTime')?.value)
      }));

    const selectedDay = moment(selectedDate).format('YYYY-MM-DD');
    const currentRequestDay = this.request?.start_date
      ? moment(this.request.start_date).format('YYYY-MM-DD')
      : null;

    // intervalli dei permessi già salvati per quel giorno; in edit salto solo il permesso già occupato
    const existingPermissionRanges = this.userEventDates
      .filter((event: any) => {
        const isCurrentEditedRequest =
          this.edit &&
          event?.eventType === 'Permesso' &&
          event?.date === currentRequestDay &&
          event?.startTime === this.request?.start_time &&
          event?.endTime === this.request?.end_time;

        return (
          event?.eventType === 'Permesso' &&
          event?.date === selectedDay &&
          event?.startTime &&
          event?.endTime &&
          !isCurrentEditedRequest
        );
      })
      .map((event: any) => {
        const [startHours, startMinutes] = event.startTime
          .split(':')
          .map(Number);
        const [endHours, endMinutes] = event.endTime.split(':').map(Number);

        return {
          startMinutes: startHours * 60 + startMinutes,
          endMinutes: endHours * 60 + endMinutes
        };
      });

    return [...formRanges, ...existingPermissionRanges];
  }

  /**
   * Converte un oggetto Date in minuti trascorsi dalla mezzanotte.
   * @param value orario da convertire
   * @returns minuti totali
   */
  private toMinutes(value: Date): number {
    return value.getHours() * 60 + value.getMinutes();
  }

  /**
   * Verifica se un intervallo candidato si sovrappone a una o più fasce occupate.
   * @param startMinutes inizio intervallo candidato
   * @param endMinutes fine intervallo candidato
   * @param ranges fasce già occupate
   * @returns true se c'è sovrapposizione
   */
  private overlapsRanges(
    startMinutes: number,
    endMinutes: number,
    ranges: Array<{ startMinutes: number; endMinutes: number }>
  ): boolean {
    return ranges.some(
      range =>
        startMinutes < range.endMinutes && range.startMinutes < endMinutes
    );
  }

  /**
   * Verifica se, dato uno start candidato, esiste almeno un endTime valido
   * (durata minima e nessuna sovrapposizione) entro il limite orario.
   * @param index indice della richiesta corrente
   * @param startMinutes start candidato in minuti
   * @returns true se almeno una fine è selezionabile
   */
  private hasValidEndForStart(index: number, startMinutes: number): boolean {
    const ranges = this.getSameDayTimeRanges(index);
    const latestEnd = 18 * 60;

    for (
      let endMinutes = startMinutes + this.MIN_DURATION;
      endMinutes <= latestEnd;
      endMinutes += 15
    ) {
      if (!this.overlapsRanges(startMinutes, endMinutes, ranges)) {
        return true;
      }
    }

    return false;
  }

  /**
   *
   * @param index passo l'indice della row dove sto settando l'orario di fine
   * @returns disabilita le ore già passate
   */
  getDefaultEndTime(index: number): Date {
    const requestGroup = (this.requestForm.get('dataArray') as FormArray).at(
      index
    );
    const startTime: Date = requestGroup.get('startTime')?.value;
    const selectedDate: Date = requestGroup.get('date')?.value;

    if (!startTime || !selectedDate) {
      // fallback a 09:30 se non ci sono dati
      return new Date(new Date().setHours(9, 30, 0, 0));
    }

    // Calcola endTime = startTime + 30 minuti
    const endTime = new Date(startTime);
    endTime.setMinutes(endTime.getMinutes() + 30);

    const today = new Date();
    const isToday = selectedDate.toDateString() === today.toDateString();

    if (isToday) {
      // Se endTime è nel passato, aggiorna a ora arrotondata + 30 minuti
      const now = new Date();
      const roundedNow = new Date(now);
      const minutes = now.getMinutes();
      const roundedMinutes = Math.ceil(minutes / 15) * 15;

      if (roundedMinutes === 60) {
        roundedNow.setHours(roundedNow.getHours() + 1);
        roundedNow.setMinutes(0);
      } else {
        roundedNow.setMinutes(roundedMinutes);
      }
      roundedNow.setSeconds(0);
      roundedNow.setMilliseconds(0);

      if (endTime < roundedNow) {
        // Sposta endTime a roundedNow + 30 minuti
        endTime.setTime(roundedNow.getTime() + 30 * 60000);
      }
    }

    return endTime;
  }

  /**
   * disabilita le ore al di fuori dell'orario lavorativo
   * @returns le ore che vanno dalle 9 alle 18
   */
  disabledHoursStart(index: number): () => number[] {
    return () =>
      Array.from({ length: 24 }, (_, hour) => hour).filter(hour => {
        if (hour < 9 || hour > 17) return true;

        const enabledQuarterMinutes = [0, 15, 30, 45].filter(min => {
          if (hour === 17 && min > 30) return false;
          const startMinutes = hour * 60 + min;
          return !this.isStartMinuteDisabled(index, hour, min, startMinutes);
        });

        return enabledQuarterMinutes.length === 0;
      });
  }

  /**
   * disabilita i minuti di inizio se sono le 17:30
   */
  disabledMinutesStart(index: number): (selectedHour: number) => number[] {
    return (selectedHour: number) => {
      if (selectedHour < 9 || selectedHour > 17) {
        return Array.from({ length: 60 }, (_, i) => i);
      }

      return Array.from({ length: 60 }, (_, minute) => {
        const allowedByStep = minute % 15 === 0;
        const allowedByBusinessRule = !(selectedHour === 17 && minute > 30);
        const startMinutes = selectedHour * 60 + minute;

        if (!allowedByStep || !allowedByBusinessRule) return minute;

        return this.isStartMinuteDisabled(
          index,
          selectedHour,
          minute,
          startMinutes
        )
          ? minute
          : -1;
      }).filter(minute => minute >= 0);
    };
  }

  /**
   * Determina se un minuto di inizio deve essere disabilitato in base a:
   * orari lavorativi, regole di business e overlap con altre richieste.
   * @param index indice della richiesta corrente
   * @param selectedHour ora selezionata
   * @param minute minuto selezionato
   * @param startMinutes valore totale in minuti del candidato
   * @returns true se il minuto va disabilitato
   */
  private isStartMinuteDisabled(
    index: number,
    selectedHour: number,
    minute: number,
    startMinutes: number
  ): boolean {
    if (selectedHour < 9 || selectedHour > 17) return true;
    if (selectedHour === 17 && minute > 30) return true;

    const ranges = this.getSameDayTimeRanges(index);
    const isInsideExistingRange = ranges.some(
      range =>
        startMinutes >= range.startMinutes && startMinutes < range.endMinutes
    );

    if (isInsideExistingRange) return true;

    return !this.hasValidEndForStart(index, startMinutes);
  }

  /*
   * @param index passo l'indice della row dove sto settando i minuti di fine
   * @returns Disabilita minuti per endTime
   */
  disabledHoursEnd(index: number): () => number[] {
    return () => {
      const control = this.dataArray.at(index);
      const start: Date = control.get('startTime')?.value;

      if (!start || !(start instanceof Date)) {
        return Array.from({ length: 24 }, (_, i) => i).filter(
          hour => hour < 9 || hour > 18
        );
      }

      return Array.from({ length: 24 }, (_, hour) => hour).filter(hour => {
        if (hour < 9 || hour > 18) return true;

        const disabledMinutes = this.disabledMinutesEnd(index)(hour);
        const quarterMinutes = [0, 15, 30, 45].filter(minute => {
          if (hour === 18 && minute > 0) return false;
          return !disabledMinutes.includes(minute);
        });

        return quarterMinutes.length === 0;
      });
    };
  }

  // Dichiaro un formArray
  get dataArray() {
    return this.requestForm.get('dataArray') as FormArray;
  }

  /**
   *
   * @param index passo l'indice della row dove sto settando i minuti di fine
   * @returns Disabilita minuti per endTime
   */
  disabledMinutesEnd(index: number): (h: number) => number[] {
    return (h: number) => {
      const start = this.dataArray.at(index).get('startTime')?.value as Date;
      if (!(start instanceof Date)) return [];
      const ranges = this.getSameDayTimeRanges(index);

      const tStart = start.getHours() * 60 + start.getMinutes();
      const minEnd = tStart + this.MIN_DURATION;
      // Oltre le 17:30 l'orario di fine sarà sempre impostato alle 18:00 e non oltre
      if (start.getHours() === 17 && start.getMinutes() === 30) {
        if (h !== 18) return Array.from({ length: 60 }, (_, i) => i);
        return Array.from({ length: 60 }, (_, i) => i).filter(m => m !== 0);
      }

      if (h === Math.floor(minEnd / 60)) {
        const minMinute = minEnd % 60;
        return Array.from({ length: 60 }, (_, i) => i).filter(m => {
          if (m < minMinute) return true;
          const candidateEnd = h * 60 + m;
          if (candidateEnd > 18 * 60) return true;
          return this.overlapsRanges(tStart, candidateEnd, ranges);
        });
      }
      // qualsiasi orario di inzio viene impostato, l'orario di fine non deve mai superare le 18:00
      if (h === 18) {
        return Array.from({ length: 60 }, (_, i) => i).filter(m => {
          if (m > 0) return true;
          const candidateEnd = h * 60 + m;
          return this.overlapsRanges(tStart, candidateEnd, ranges);
        });
      }

      return Array.from({ length: 60 }, (_, i) => i).filter(m => {
        const candidateEnd = h * 60 + m;
        if (candidateEnd <= minEnd || candidateEnd > 18 * 60) return true;
        return this.overlapsRanges(tStart, candidateEnd, ranges);
      });
    };
  }

  /**
   * limita il testo della descrizione non oltre i 150 caratteri
   */
  limitTextLength(index: number): void {
    const control = this.dataArray.at(index)?.get('notes');
    if (control) {
      const text = control.value || '';
      if (text.length > 250) {
        control.setValue(text.substring(0, 250), { emitEvent: false });
      }
    }
  }

  /**
   *
   * @param index passo l'idice della row che sto cambiando nella selezione del giorno
   */
  onDateChange(index: number) {
    const control = (this.requestForm.get('dataArray') as FormArray).at(index);
    control.get('startTime')?.reset();
    control.get('endTime')?.reset();
    this.loadUserEventDates(control.get('date')?.value ?? null);
  }

  /**
   *
   * @param index passo l'idice della row dell'orario di inizio che voglio resettare e svuoto l'input dell'orario di fine
   */
  onChangeTime(index: number) {
    this.normalizeStartTimeSelection(index);

    if (this.timePickerOpen) {
      const control = (this.requestForm.get('dataArray') as FormArray).at(
        index
      );
      // resetta le ora di fine quando vengono modificate quelle di inizio
      control.get('endTime')?.reset();
    }
  }

  /**
   * Normalizza l'orario di fine dopo la selezione dal time picker
   * scegliendo il primo minuto valido nell'ora selezionata.
   * @param index indice della richiesta corrente
   */
  onChangeEndTime(index: number) {
    this.normalizeEndTimeSelection(index);
  }

  /**
   * Normalizza lo startTime quando il time picker imposta un minuto non valido
   * (es. selezione sola ora -> :00) scegliendo il primo slot disponibile.
   * @param index indice della richiesta corrente
   */
  private normalizeStartTimeSelection(index: number): void {
    const control = this.dataArray.at(index);
    const start = control?.get('startTime')?.value as Date;

    if (!(start instanceof Date)) return;

    const selectedHour = start.getHours();
    const selectedMinute = start.getMinutes();
    const disabledMinutes = this.disabledMinutesStart(index)(selectedHour);

    if (!disabledMinutes.includes(selectedMinute)) return;

    const nextValidMinute = [0, 15, 30, 45].find(
      minute => !disabledMinutes.includes(minute)
    );

    if (nextValidMinute === undefined) return;

    const normalized = new Date(start);
    normalized.setMinutes(nextValidMinute, 0, 0);
    control.get('startTime')?.setValue(normalized);
  }

  /**
   * Normalizza l'endTime quando il time picker imposta un minuto non valido
   * (es. selezione sola ora -> :00) scegliendo il primo slot disponibile.
   * @param index indice della richiesta corrente
   */
  private normalizeEndTimeSelection(index: number): void {
    const control = this.dataArray.at(index);
    const end = control?.get('endTime')?.value as Date;

    if (!(end instanceof Date)) return;

    const selectedHour = end.getHours();
    const selectedMinute = end.getMinutes();
    const disabledMinutes = this.disabledMinutesEnd(index)(selectedHour);

    if (!disabledMinutes.includes(selectedMinute)) return;

    const nextValidMinute = [0, 15, 30, 45].find(minute => {
      if (selectedHour === 18 && minute > 0) return false;
      return !disabledMinutes.includes(minute);
    });

    if (nextValidMinute === undefined) return;

    const normalized = new Date(end);
    normalized.setMinutes(nextValidMinute, 0, 0);
    control.get('endTime')?.setValue(normalized);
  }

  /**
   * Salva chiamata delle richieste
   */
  saveRequestPermission() {
    // Converto la data poco prima di inviarla al BE
    const formData = this.requestForm.value;
    const payload = {
      ...formData,
      user_id: this.user?.id,
      date_array: formData.dataArray.map((item: any) => ({
        startDate: moment(item.date).format('YYYY/MM/DD'),
        start_time: moment(item.startTime).format('HH:mm'),
        end_time: moment(item.endTime).format('HH:mm'),
        notes: item.notes ?? null
      }))
    };
    // cancello dati che non voglio passare al BE
    delete payload.dataArray;
    // mando il form al banckend
    this.rs.storeRequest(payload).subscribe({
      next: () => {
        // Richiamo l'alert direttamente nell'AlertService
        const text = 'di permesso';
        this.alertService.showSuccessRequestAlert(text);
        //recupero la prima data degli eventi creati
        const firstDate = new Date(payload.date_array[0].startDate);
        //Chiudo la modale
        this.saved.emit({ success: true, date: firstDate });
        this.modalRef.close();
      },
      error: () => {
        this.alertService.showErrorRequestAlert();
      }
    });
  }

  /**
   * Chiamata che serve per modificare una richiesta di permesso
   */
  updateRequest() {
    const formData = this.requestForm.value;
    const obj = {
      event_id: this.request.id,
      event_type_id: 8,
      notes: formData.dataArray[0]?.notes ?? null,
      start_time: moment(formData.dataArray[0].startTime).format('HH:mm'),
      end_time: moment(formData.dataArray[0].endTime).format('HH:mm')
    };
    this.rs.updateRequest(obj).subscribe({
      next: () => {
        // Richiamo l'alert direttamente nell'AlertService
        const text = 'di permesso';
        this.alertService.showEditRequestAlert(text);
        this.saved.emit({ success: true, date: formData.dataArray[0].date });
        this.modalRef.close();
      },
      error: () => {
        this.alertService.showErrorRequestAlert();
      }
    });
  }

  /**
   * Cancella la singola riga del requestForm della richiesta permesso
   */
  deleteRequest(target: number | AbstractControl) {
    const deleteRow = this.requestForm.get('dataArray') as FormArray;
    const index =
      typeof target === 'number' ? target : deleteRow.controls.indexOf(target);

    if (index >= 0 && deleteRow.length > index) {
      deleteRow.removeAt(index);
      deleteRow.controls.forEach(control => {
        control.get('startTime')?.updateValueAndValidity({ emitEvent: false });
        control.get('endTime')?.updateValueAndValidity({ emitEvent: false });
      });
      deleteRow.updateValueAndValidity({ emitEvent: false });
      this.currentStep = Math.min(this.currentStep, this.totalSteps - 1);
    }
  }

  prevStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  nextStep(): void {
    if (this.currentStep < this.totalSteps - 1) {
      this.currentStep++;
    }
  }

  onOpenTimePicker(open: boolean) {
    if (open) this.timePickerOpen = open;
  }

  /**
   * chiudi modale
   */
  closeModal() {
    this.modalRef.close();
  }

  /**
   *
   * @param date passo la data di oggi
   * @returns mi torna la data, il mese e l'anno corrente
   */
  isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }
}
